import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X, ArrowRight, ArrowUpRight, Loader2, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import Logo from './Logo';
import supplierLogo from '@/images/partners/supplier.svg';
import wholesalerLogo from '@/images/partners/wholesaler.svg';
import { useIsLoggedIn } from '@/lib/auth';
import { useOpenAdmin } from '@/lib/openAdmin';
import { TRIAL_DAYS } from '@/seo/site';

const LINKS = [
  { anchor: 'products', label: 'Продукт' },
  { anchor: 'features', label: 'Возможности' },
  { anchor: 'map', label: 'Заведения' },
  { anchor: 'pricing', label: 'Цена' },
  { anchor: 'faq', label: 'Вопросы' },
];
// Стабильная ссылка, иначе эффект наблюдателя пересоздавался бы на каждом рендере.
const SECTION_IDS = LINKS.map((l) => l.anchor);

// Партнёрские продукты со своими логотипами (SVG без подложки, в фирменных цветах).
const PARTNERS = [
  {
    to: '/suppliers',
    label: 'Поставщикам',
    hint: 'Приём заказов от заведений, каталог, отгрузки',
    logo: supplierLogo,
  },
  {
    to: '/wholesalers',
    label: 'Оптовикам',
    hint: 'Оптовые прайсы и заявки от ресторанов',
    logo: wholesalerLogo,
  },
];

/** Какая секция главной сейчас в зоне чтения, для подсветки пункта навигации. */
function useActiveSection(ids: string[], enabled: boolean) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === 'undefined') {
      setActive(null);
      return;
    }
    const els = ids.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;

    const visible = new Map<string, number>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) visible.set(e.target.id, e.intersectionRatio);
          else visible.delete(e.target.id);
        }
        // Секция считается активной, пока её верх ниже линии ~35% высоты экрана.
        let best: string | null = null;
        let bestTop = Number.NEGATIVE_INFINITY;
        for (const el of els) {
          const top = el.getBoundingClientRect().top;
          if (top <= window.innerHeight * 0.35 && top > bestTop) {
            bestTop = top;
            best = el.id;
          }
        }
        setActive(best);
      },
      { rootMargin: '-35% 0px -40% 0px', threshold: [0, 0.1, 0.5] },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ids, enabled]);

  return active;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const authed = useIsLoggedIn();
  const location = useLocation();
  const navigate = useNavigate();
  const { open: openAdmin, busy: ssoBusy } = useOpenAdmin();
  const isHome = location.pathname === '/';
  const active = useActiveSection(SECTION_IDS, isHome);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Блокировка скролла body при открытом мобильном меню.
  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [open]);

  // Закрываем мобильное меню при переходе на другую страницу.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const jumpTo = (anchor: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${anchor}`);
    } else {
      navigate(`/#${anchor}`);
    }
  };

  const partnerActive = PARTNERS.some((p) => location.pathname.startsWith(p.to));

  return (
    <header className="sticky top-0 z-40 px-3 pt-3 sm:px-5 sm:pt-4">
      {/* Плавающая пилюля поверх контента, а не сплошная полоса на всю ширину. */}
      <div
        className={clsx(
          'mx-auto flex h-[60px] max-w-content items-center justify-between rounded-full border pl-4 pr-2 transition-[background-color,box-shadow,border-color] duration-300 sm:pl-5 sm:pr-2.5',
          scrolled
            ? 'border-ink/10 bg-white/85 shadow-glass backdrop-blur-xl'
            : 'border-ink/[0.07] bg-white/60 backdrop-blur-lg',
        )}
      >
        <a href="/" aria-label="Resstro, на главную" className="flex items-center">
          <Logo />
        </a>

        {/* Пилюльная навигация. Активная секция подсвечивается по мере скролла. */}
        <nav className="hidden items-center gap-0.5 md:flex" aria-label="Разделы">
          {LINKS.map((l) => (
            <a
              key={l.anchor}
              href={`/#${l.anchor}`}
              onClick={jumpTo(l.anchor)}
              aria-current={active === l.anchor ? 'true' : undefined}
              className={clsx(
                'rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors duration-300',
                active === l.anchor ? 'bg-ink/[0.06] text-ink' : 'text-ink/60 hover:bg-ink/[0.04] hover:text-ink',
              )}
            >
              {l.label}
            </a>
          ))}

          <Link
            to="/downloads"
            className={clsx(
              'rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors duration-300',
              location.pathname === '/downloads'
                ? 'bg-ink/[0.06] text-ink'
                : 'text-ink/60 hover:bg-ink/[0.04] hover:text-ink',
            )}
          >
            Загрузки
          </Link>

          {/* Партнёрам: выпадающее меню по hover/focus с логотипами продуктов. */}
          <div className="group relative">
            <button
              type="button"
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors duration-300',
                partnerActive ? 'bg-ink/[0.06] text-ink' : 'text-ink/60 group-hover:bg-ink/[0.04] group-hover:text-ink',
              )}
              aria-haspopup="menu"
            >
              Партнёрам
              <ChevronDown
                size={14}
                strokeWidth={2}
                className="transition-transform duration-200 group-hover:rotate-180 group-focus-within:rotate-180"
              />
            </button>
            <div className="invisible absolute left-1/2 top-full z-50 w-[340px] -translate-x-1/2 pt-3 opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <div className="rounded-2xl border border-ink/10 bg-white p-1.5 shadow-lift">
                {PARTNERS.map(({ to, label, hint, logo }) => (
                  <Link
                    key={to}
                    to={to}
                    className="flex items-start gap-3.5 rounded-xl px-3 py-3 transition-colors hover:bg-canvas"
                  >
                    <img src={logo} alt="" width={36} height={36} className="mt-0.5 h-9 w-9 shrink-0 select-none" draggable={false} />
                    <span className="min-w-0">
                      <span className="block text-[14px] font-medium text-ink">{label}</span>
                      <span className="mt-0.5 block text-[12.5px] leading-snug text-ink/55">{hint}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="hidden items-center gap-1.5 md:flex">
          {authed ? (
            <>
              <Link to="/cabinet" className="btn-ghost px-4 py-2.5 text-[13px]">
                Кабинет
              </Link>
              <button
                type="button"
                onClick={openAdmin}
                disabled={ssoBusy}
                className="btn-dark h-11 px-5 py-0 text-[13.5px] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {ssoBusy ? (
                  <>
                    <Loader2 size={14} strokeWidth={2} className="animate-spin" />
                    Открываем…
                  </>
                ) : (
                  <>
                    Админ-панель
                    <span className="btn-icon !bg-orange">
                      <ArrowUpRight size={14} strokeWidth={2.2} />
                    </span>
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="rounded-full px-4 py-2.5 text-[13px] font-medium text-ink/70 transition-colors hover:bg-ink/[0.04] hover:text-ink"
              >
                Войти
              </Link>
              <Link to="/signup" className="btn-dark h-11 px-5 py-0 text-[13.5px]">
                Начать бесплатно
                <span className="btn-icon !bg-orange">
                  <ArrowRight size={14} strokeWidth={2.2} />
                </span>
              </Link>
            </>
          )}
        </div>

        <button
          type="button"
          className="grid h-11 w-11 place-items-center rounded-full text-ink transition-colors hover:bg-ink/[0.05] md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={open}
        >
          {open ? <X size={22} strokeWidth={1.6} /> : <Menu size={22} strokeWidth={1.6} />}
        </button>
      </div>

      {/* Полноэкранное мобильное меню рендерится в body через Portal,
         иначе backdrop-blur на header ломает position:fixed дочерних */}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex flex-col overflow-y-auto bg-canvas md:hidden"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex h-[76px] items-center justify-between px-6 pt-3">
              <a href="/" aria-label="Resstro, на главную" className="flex items-center" onClick={() => setOpen(false)}>
                <Logo />
              </a>
              <button
                type="button"
                className="grid h-11 w-11 place-items-center rounded-full bg-white text-ink shadow-hair"
                onClick={() => setOpen(false)}
                aria-label="Закрыть меню"
              >
                <X size={20} strokeWidth={1.7} />
              </button>
            </div>

            <nav className="mt-6 flex flex-col px-6" aria-label="Разделы">
              {LINKS.map((l) => (
                <a
                  key={l.anchor}
                  href={`/#${l.anchor}`}
                  onClick={jumpTo(l.anchor)}
                  className="block border-b border-line py-4 text-[26px] font-medium tracking-tightest text-ink"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/downloads"
                onClick={() => setOpen(false)}
                className="block border-b border-line py-4 text-[26px] font-medium tracking-tightest text-ink"
              >
                Загрузки
              </Link>

              <p className="mt-8 text-[13px] font-medium text-ink/50">Партнёрам</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {PARTNERS.map(({ to, label, logo }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 rounded-2xl border border-line bg-white px-4 py-3.5 text-[15px] font-medium text-ink"
                  >
                    <img src={logo} alt="" width={28} height={28} className="h-7 w-7 shrink-0 select-none" draggable={false} />
                    {label}
                  </Link>
                ))}
              </div>
            </nav>

            <div className="mt-auto flex flex-col gap-3 px-6 py-8">
              {authed ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      void openAdmin();
                    }}
                    disabled={ssoBusy}
                    className="btn-dark w-full px-6 py-4 text-[15px] disabled:opacity-60"
                  >
                    {ssoBusy ? (
                      <>
                        <Loader2 size={16} strokeWidth={2} className="animate-spin" />
                        Открываем админку…
                      </>
                    ) : (
                      <>
                        Открыть админ-панель
                        <span className="btn-icon !bg-orange">
                          <ArrowUpRight size={15} strokeWidth={2.2} />
                        </span>
                      </>
                    )}
                  </button>
                  <Link to="/cabinet" onClick={() => setOpen(false)} className="btn-outline w-full px-6 py-4 text-[15px]">
                    Личный кабинет
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/signup" onClick={() => setOpen(false)} className="btn-dark w-full px-6 py-4 text-[15px]">
                    Начать {TRIAL_DAYS} дней бесплатно
                    <span className="btn-icon !bg-orange">
                      <ArrowRight size={15} strokeWidth={2.2} />
                    </span>
                  </Link>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline w-full px-6 py-4 text-[15px]">
                    Войти в кабинет
                  </Link>
                </>
              )}
              <p className="mt-2 text-center text-[12px] text-ink/45">resstrosaas@mail.ru · +7 989 473-43-22</p>
            </div>
          </div>,
          document.body,
        )}
    </header>
  );
}
